import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  Package,
  Terminal,
  type LucideIcon,
} from "lucide-react";

type Method = "one-liner" | "npm";
type Os = "unix" | "windows";

interface Command {
  raw: string;
  prompt: string;
}

const METHODS: Array<{ key: Method; Icon: LucideIcon; label: string }> = [
  { key: "one-liner", Icon: Terminal, label: "One-liner" },
  { key: "npm", Icon: Package, label: "npm" },
];

function getCommands(
  t: (key: string, opts?: Record<string, unknown>) => string,
  method: Method,
  os: Os,
): Command {
  if (method === "one-liner") {
    if (os === "unix") {
      return {
        raw: "curl -fsSL https://tokenbuddy.ai/install.sh | bash",
        prompt: t("install.oneLiner.unix.prompt"),
      };
    }
    return {
      raw: 'powershell -c "irm https://tokenbuddy.ai/install.ps1 | iex"',
      prompt: t("install.oneLiner.windows.prompt"),
    };
  }

  return {
    raw: "npm i -g @tokenbuddy/tokenbuddy && tb init",
    prompt: t("install.npm.prompt"),
  };
}

/**
 * Final install card. Shown at the bottom of the landing page (the conversion
 * ask). OS toggle (macOS/Linux vs Windows) and method toggle (one-liner vs
 * npm) drive a single rendered command. The copy button writes the raw
 * command (not the i18n'd prompt, which is a display string).
 */
export function FinalCta() {
  const { t } = useTranslation();
  const [method, setMethod] = useState<Method>("one-liner");
  const [os, setOs] = useState<Os>("unix");
  const [copied, setCopied] = useState(false);

  const active = getCommands(t, method, os);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(active.raw);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* best effort */
    }
  };

  return (
    <div className="page-container">
      <div className="rounded-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-panel)] p-4 shadow-[0_12px_36px_rgba(60,41,112,0.07)] md:p-5">
        <div className="mx-auto mb-5 max-w-[720px] text-center">
          <h2 className="text-[26px] font-bold leading-8 tracking-[0] text-[var(--color-ink-strong)] md:text-[34px] md:leading-10">
            {t("install.page.title")}
          </h2>
          <p className="mx-auto mt-2 max-w-[62ch] text-[13.5px] leading-6 text-[var(--color-muted)] md:text-[15px]">
            {t("install.page.lede")}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-3">
          <div
            className="flex min-h-11 items-center gap-1 rounded-[var(--radius-md)] border border-[var(--color-line)] bg-[var(--color-panel-subtle)] p-0.5 text-[12px]"
            role="group"
            aria-label={t("install.os.unix")}
          >
            {(["unix", "windows"] as const).map((target) => (
              <button
                key={target}
                type="button"
                onClick={() => setOs(target)}
                aria-pressed={os === target}
                className={[
                  "min-h-10 rounded-[var(--radius-sm)] px-3 py-1.5 font-bold transition-colors",
                  os === target
                    ? "bg-[var(--color-ink)] text-white"
                    : "text-[var(--color-muted)] hover:text-[var(--color-ink-mid)]",
                ].join(" ")}
              >
                {t(`install.os.${target}`)}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-4 overflow-hidden rounded-[var(--radius-lg)] border border-white/10 bg-[var(--color-ink-deep)] shadow-[0_16px_38px_rgba(17,24,39,0.18)]">
          <div className="flex flex-wrap items-center gap-1 border-b border-white/10 bg-black/20 px-2 py-2">
            {METHODS.map(({ key, Icon, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setMethod(key)}
                aria-pressed={method === key}
                className={[
                  "inline-flex min-h-10 items-center gap-1.5 rounded-[var(--radius-md)] px-3 text-[12.5px] font-bold transition-colors",
                  method === key
                    ? "bg-white text-[var(--color-ink-strong)]"
                    : "text-white/62 hover:bg-white/10 hover:text-white",
                ].join(" ")}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                {label}
              </button>
            ))}
          </div>

          <div className="grid min-w-0 gap-3 p-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-center">
            <code className="num min-w-0 overflow-x-auto whitespace-nowrap rounded-[var(--radius-md)] bg-black/28 px-3 py-3 text-left font-mono text-[12px] leading-6 text-white md:text-[13px]">
              <span className="select-none font-bold text-[var(--color-success)]">$ </span>
              {active.prompt.replace(/^\$\s*/, "")}
            </code>

            <button
              type="button"
              onClick={onCopy}
              className="inline-flex min-h-11 items-center justify-center gap-1.5 rounded-[var(--radius-md)] bg-white px-3 text-[12.5px] font-bold text-[var(--color-ink-strong)] transition-colors hover:bg-[var(--color-primary-soft)] active:scale-[0.98]"
            >
              {copied ? (
                <Check className="h-3.5 w-3.5 text-[var(--color-success)]" aria-hidden="true" />
              ) : (
                <Copy className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <span>{copied ? t("install.copied") : t("install.copy")}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
